'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetWinnerRow, useGetMarketChart, useGetWinner } from '@morfi/backend-sdk-v2/client';
import { type ReactNode, useEffect } from 'react';
import { LIVE_WINDOW_SECONDS, resolveAvailableInterval } from '@/lib/chart/chart-factory';
import { logger } from '@/lib/telemetry/logger';
import { useChartIntentStore, type WinnerChartIntentSelection } from '@/stores/chart-intent';
import { useWinnerLiveTrades } from './use-winner-live-trades';
import { type WinnerChartRequest, winnerAvailableIntervals, winnerChartRequest } from './winner-chart-request';
export function WinnerChartIntent(t0) {
  const $ = _c(9);
  const {
    selection
  } = t0;
  let t1;
  if ($[0] !== selection.outcomeMint) {
    t1 = {
      outcomeMint: selection.outcomeMint
    };
    $[0] = selection.outcomeMint;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const winner = useGetWinner(t1);
  let t2;
  let t3;
  if ($[2] !== selection.outcomeMint || $[3] !== winner.isError) {
    t2 = () => {
      if (!winner.isError) {
        return;
      }
      logger.warn("chart.intent_winner_load_failed", {
        outcomeMint: selection.outcomeMint
      });
    };
    t3 = [winner.isError, selection.outcomeMint];
    $[2] = selection.outcomeMint;
    $[3] = winner.isError;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== selection || $[7] !== winner.data) {
    t4 = winner.data?.parentMarket === undefined ? null : <WinnerIntentQueries selection={selection} winner={winner.data} />;
    $[6] = selection;
    $[7] = winner.data;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function WinnerIntentQueries(t0) {
  const $ = _c(15);
  const {
    selection,
    winner
  } = t0;
  const anchor = Math.floor(selection.startedAt / 60000) * 60000;
  let t1;
  if ($[0] !== anchor || $[1] !== selection.interval || $[2] !== selection.renderMode || $[3] !== selection.yAxisProperty || $[4] !== winner.graduatedAt || $[5] !== winner.mint || $[6] !== winner.outcomeIndex || $[7] !== winner.parentMarket.pubkey || $[8] !== winner.parentMarket.tradingStartedAt) {
    const interval = resolveAvailableInterval(selection.interval, winnerAvailableIntervals(winner.graduatedAt, anchor));
    t1 = winnerChartRequest(winner.parentMarket.pubkey, winner.mint, winner.outcomeIndex, interval, anchor, winner.parentMarket.tradingStartedAt, selection.yAxisProperty, selection.renderMode, 180);
    $[0] = anchor;
    $[1] = selection.interval;
    $[2] = selection.renderMode;
    $[3] = selection.yAxisProperty;
    $[4] = winner.graduatedAt;
    $[5] = winner.mint;
    $[6] = winner.outcomeIndex;
    $[7] = winner.parentMarket.pubkey;
    $[8] = winner.parentMarket.tradingStartedAt;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const request = t1;
  let t2;
  if ($[10] !== anchor || $[11] !== request || $[12] !== selection.key || $[13] !== winner) {
    t2 = request.kind === "live" ? <WinnerIntentTape winner={winner} anchor={anchor} /> : <WinnerIntentHistory request={request} outcomeMint={winner.mint} selectionKey={selection.key} />;
    $[10] = anchor;
    $[11] = request;
    $[12] = selection.key;
    $[13] = winner;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}
function WinnerIntentHistory(t0) {
  const $ = _c(11);
  const {
    request,
    outcomeMint,
    selectionKey
  } = t0;
  const store = useChartIntentStore();
  const query = useGetMarketChart(request.queryArgs);
  const {
    pubkey: marketPubkey,
    property
  } = request.queryArgs;
  const {
    interval
  } = request;
  let t1;
  let t2;
  if ($[0] !== interval || $[1] !== marketPubkey || $[2] !== outcomeMint || $[3] !== property || $[4] !== query.data || $[5] !== query.isError || $[6] !== query.isFetchedAfterMount || $[7] !== selectionKey || $[8] !== store) {
    t1 = () => {
      if (!query.isError || !query.isFetchedAfterMount || query.data !== undefined) {
        return;
      }
      logger.warn("chart.intent_snapshot_failed", {
        marketPubkey,
        outcomeMint,
        interval,
        property
      });
      store.getState().clear(selectionKey);
    };
    t2 = [query.isError, query.isFetchedAfterMount, query.data, store, selectionKey, marketPubkey, property, interval, outcomeMint];
    $[0] = interval;
    $[1] = marketPubkey;
    $[2] = outcomeMint;
    $[3] = property;
    $[4] = query.data;
    $[5] = query.isError;
    $[6] = query.isFetchedAfterMount;
    $[7] = selectionKey;
    $[8] = store;
    $[9] = t1;
    $[10] = t2;
  } else {
    t1 = $[9];
    t2 = $[10];
  }
  useEffect(t1, t2);
  return null;
}
function WinnerIntentTape(t0) {
  const $ = _c(6);
  const {
    winner,
    anchor
  } = t0;
  const t1 = anchor - LIVE_WINDOW_SECONDS * 1000;
  let t2;
  if ($[0] !== t1 || $[1] !== winner.graduatedAt || $[2] !== winner.mint || $[3] !== winner.outcomeIndex || $[4] !== winner.parentMarket.pubkey) {
    t2 = {
      marketPubkey: winner.parentMarket.pubkey,
      outcomeMint: winner.mint,
      originalOutcomeIndex: winner.outcomeIndex,
      tradesFrom: winner.graduatedAt,
      chartHistoryCutoffMs: t1,
      liveChartEnabled: true
    };
    $[0] = t1;
    $[1] = winner.graduatedAt;
    $[2] = winner.mint;
    $[3] = winner.outcomeIndex;
    $[4] = winner.parentMarket.pubkey;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useWinnerLiveTrades(t2);
  return null;
}